import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import GetAppIcon from '@material-ui/icons/GetApp';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import DeleteDialog from '../../../../components/DeleteDialog';
import {
  fetchGifQueue,
  deleteGifJob,
  processGifQueue,
} from '../../redux/actions/ai';

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
  statusBadge: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  videoCount: {
    fontWeight: 500,
  },
}));

const displayStatus = (status) => {
  if (status === 'processing') return 'in progress';
  return status;
};

const statusColor = (status) => {
  const ds = displayStatus(status);
  if (ds === 'completed') return 'green';
  if (ds === 'failed') return 'red';
  if (ds === 'in progress') return 'orange';
  return 'orange'; // pending
};

export default function GifQueue() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { gifQueue, fetching, actionLoading } = useSelector(
    ({ admin }) => admin.ai
  );

  const [sort, setSort] = useState({ orderBy: 'updatedAt', orderDir: 'desc' });
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);

  useEffect(() => {
    dispatch(fetchGifQueue(1, 25, false, sort.orderBy, sort.orderDir));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    let interval;
    const hasProcessing = (gifQueue?.docs || []).some(
      (job) => job.status === 'pending' || job.status === 'processing' || job.status === 'in progress'
    );
    if (hasProcessing) {
      interval = setInterval(() => {
        dispatch(fetchGifQueue(gifQueue?.page || 1, gifQueue?.limit || 25, true, sort.orderBy, sort.orderDir));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gifQueue, dispatch, sort]);

  const loadPage = (page, sortOptions = {}) => {
    const nextSort = sortOptions.orderBy ? sortOptions : sort;
    if (sortOptions.orderBy) setSort(sortOptions);
    dispatch(
      fetchGifQueue(
        page,
        gifQueue?.limit || 25,
        false,
        nextSort.orderBy,
        nextSort.orderDir,
      )
    );
  };

  const hasSelectedQueues = selectedQueueIds.length > 0;

  const handleProcessQueue = async () => {
    await dispatch(processGifQueue(selectedQueueIds));
    setSelectedQueueIds([]);
    dispatch(fetchGifQueue(1, gifQueue?.limit || 25));
  };

  const headCells = [
    {
      id: 'checkbox',
      label: '',
    },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      formatter: (row) =>
        row.updatedAt ? dayjs(row.updatedAt).format('MMM D, YYYY h:mm A') : '—',
    },
    {
      id: 'videos',
      label: 'Videos',
      disableSorting: true,
      formatter: (row) => {
        const total = row.videos?.length || 0;
        const done = row.videos?.filter((v) => v.status === 'completed').length || 0;
        const failed = row.videos?.filter((v) => v.status === 'failed').length || 0;
        const lengths = [
          ...new Set((row.videos || []).map((video) => video.trimDuration).filter((seconds) => seconds > 0)),
        ];
        return (
          <span className={classes.videoCount}>
            {done}/{total} done
            {lengths.length > 0 && (
              <span style={{ marginLeft: 6 }}>{lengths.join(', ')}s</span>
            )}
            {failed > 0 && (
              <span style={{ color: 'red', marginLeft: 4 }}>({failed} failed)</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      formatter: (row) => (
        <span
          className={classes.statusBadge}
          style={{ color: statusColor(row.status) }}
        >
          {displayStatus(row.status)}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSorting: true,
      actions: [
        {
          label: 'Download GIFs',
          action: (r) => {
            // Download individual completed GIF URLs
            const completed = (r.videos || []).filter((v) => v.gifUrl);
            if (completed.length === 0) return;
            completed.forEach((v, index) => {
              setTimeout(() => {
                const link = document.createElement('a');
                link.href = v.gifUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = v.gifKey ? v.gifKey.split('/').pop() : 'animated.gif';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }, index * 500);
            });
          },
          icon: <GetAppIcon />,
          disabled: (r) =>
            !(r.videos || []).some((v) => v.gifUrl),
        },
        {
          label: 'Remove',
          action: (r) => {
            setDeleteId(r._id);
            setOpenDeleteDialog(true);
          },
          icon: <DeleteForeverIcon />,
          disabled: () => false,
        },
      ],
    },
  ];

  return (
    <div className={classes.root}>
      {(fetching || actionLoading) && (
        <Loader fetching={fetching || actionLoading} />
      )}
      <PageHeader
        disableSearch
        back
        handleBack={() => history.push('/superadmin/ai')}
        title="GIF Generation Queue"
        options={
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcessQueue}
            disabled={!hasSelectedQueues || actionLoading}
          >
            Create GIF
          </Button>
        }
      />

      {gifQueue && gifQueue.docs && gifQueue.docs.length > 0 ? (
        <Grid container direction="row" justifyContent="flex-start" spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={gifQueue}
              headCells={headCells}
              handlePage={loadPage}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handleOrder={(d) => loadPage(gifQueue.page, d)}
              selected={selectedQueueIds}
              onSelectionChange={setSelectedQueueIds}
            />
          </Grid>
        </Grid>
      ) : (
        <Typography color="textSecondary" style={{ marginTop: 16 }}>
          {fetching ? 'Loading...' : 'No GIFs in the queue yet.'}
        </Typography>
      )}

      <DeleteDialog
        open={openDeleteDialog}
        content={'Are you sure you want to remove this job from the queue?'}
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={() => {
          dispatch(deleteGifJob(deleteId));
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
    </div>
  );
}
